import { useLocation, Navigate, Link } from "react-router-dom";
import SEO from "@/components/SEO";
import PageHeader from "@/components/PageHeader";
import VarietyGrid from "@/components/VarietyGrid";
import RootstockGuide from "@/components/RootstockGuide";
import { Reveal, Overline } from "@/components/Reveal";
import { getVenture, VENTURES, APPLE_VARIETIES, MANGO_VARIETIES } from "@/lib/data";
import { SITE_URL } from "@/lib/site";
import { useLang } from "@/i18n";
import { ArrowRight } from "lucide-react";

/**
 * Generic, data-driven page for a business vertical.
 * Renders from the VENTURES model in data.js (with per-language overrides from
 * i18n `data.ventures`), so new verticals need no new page code — just a new
 * entry in the data file. The slug is derived from the pathname (routes are
 * fixed, e.g. /fruit-trading).
 */
export default function VerticalPage() {
  const { pathname } = useLocation();
  const { t, data, lang } = useLang();
  const slug = pathname.replace(/^\/+/, "");
  const v = getVenture(slug);
  if (!v) return <Navigate to="/" replace />;

  const dv = data.ventures?.[slug] || {};
  const title = dv.title || v.title;
  const sections = dv.sections || v.sections;
  const highlights = dv.highlights || v.highlights;
  const tags = dv.tags || v.tags;
  const ctaHeading = t("vertical.ctaHeading").replace("{venture}", lang === "en" ? title.toLowerCase() : title);
  const others = VENTURES.filter((x) => x.slug !== slug);

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: v.title,
    serviceType: v.title,
    provider: { "@type": "Organization", name: "Farmsons", url: SITE_URL },
    areaServed: "Worldwide",
    description: v.seo.description,
  };

  return (
    <>
      <SEO title={v.seo.title} description={v.seo.description} keywords={v.seo.keywords} jsonLd={schema} />
      <PageHeader overline={t("vertical.overline")} status={dv.status || v.status} title={title} lede={dv.intro || v.intro} image={v.image} imageAlt={v.imageAlt} />

      {/* Content sections */}
      <section className="py-16 md:py-24 bg-[#fdfbf5]">
        <div className="w-full max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="flex flex-wrap gap-2 mb-16">
            {tags.map((tag) => <span key={tag} className="text-xs font-medium text-[#5f6b57] border border-[#1d3a1f]/10 px-4 py-2">{tag}</span>)}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-16 gap-y-14">
            {sections.map((s, i) => (
              <Reveal key={s.heading} delay={i * 0.05}>
                <article className="border-t border-[#1d3a1f]/10 pt-6">
                  <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight">{s.heading}</h2>
                  <p className="mt-4 text-[#5f6b57] text-base md:text-lg leading-relaxed">{s.body}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-20 md:py-28 bg-[#ffffff] border-y border-[#1d3a1f]/5">
        <div className="w-full max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal><Overline>{highlights.title}</Overline></Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {highlights.items.map((h, i) => (
              <Reveal key={h.title} delay={i * 0.06}>
                <div className="h-full bg-[#fdfbf5] rounded-2xl border border-[#1d3a1f]/10 card-soft hover:border-[#d3a53a] hover:-translate-y-1.5 hover:shadow-xl transition-all duration-300 p-6">
                  <h3 className="font-display font-bold text-lg text-[#d3a53a]">{h.title}</h3>
                  <p className="mt-3 text-sm text-[#5f6b57] leading-relaxed">{h.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Variety depth (fruit trading) / rootstock guide */}
      {slug === "fruit-trading" && (
        <>
          <VarietyGrid
            overline={t("vertical.appleOverline")}
            title={t("vertical.appleTitle")}
            intro={t("vertical.appleIntro")}
            varieties={APPLE_VARIETIES}
          />
          <div className="bg-white border-y border-[#1d3a1f]/10">
            <VarietyGrid
              overline={t("vertical.mangoOverline")}
              title={t("vertical.mangoTitle")}
              intro={t("vertical.mangoIntro")}
              varieties={MANGO_VARIETIES}
            />
          </div>
        </>
      )}
      {slug === "rootstock" && <RootstockGuide />}

      {/* Cross-links + CTA */}
      <section className="py-20 md:py-28 bg-[#fdfbf5]">
        <div className="w-full max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 pb-14 border-b border-[#1d3a1f]/10">
            <h2 className="font-display font-bold text-3xl md:text-4xl max-w-[16ch]">{ctaHeading}</h2>
            <Link to="/contact" className="group inline-flex items-center gap-2 bg-[#d3a53a] text-[#173a1e] font-bold px-8 py-4 rounded-full hover:bg-[#b98a26] transition-colors duration-200 shrink-0">
              {t("common.quote")} <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </div>
          <div className="mt-10 flex flex-wrap gap-6">
            <span className="text-sm text-[#5f6b57]">{t("vertical.exploreOthers")}</span>
            {others.map((o) => (
              <Link key={o.slug} to={`/${o.slug}`} className="text-sm font-bold text-[#1d3a1f]/70 hover:text-[#d3a53a] transition-colors">{(data.ventures?.[o.slug]?.title) || o.title} →</Link>
            ))}
            <Link to="/sourcing" className="text-sm font-bold text-[#1d3a1f]/70 hover:text-[#d3a53a] transition-colors">{t("vertical.sourcingLink")} →</Link>
          </div>
        </div>
      </section>
    </>
  );
}
